<script lang="ts">
	import { FULL_SITE_URL, SITE_NAME } from '$lib/statics';
	import { UrlUtil } from '$lib/utils/url.util';

	const LAST_UPDATED = 'October 2026';

	const meta = {
		title: `Privacy Policy - ${SITE_NAME}`,
		description: `Understand how ${SITE_NAME} handles your data and respects your privacy.`,
		canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toPrivacyPolicy().href}`,
		imageUrl: UrlUtil.getStaticFileFullUrl('images', 'scenic-coastal-city-travel-destinations-brazil-banner.jpg')
	};
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />
	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />
</svelte:head>

<div class="container mx-auto min-h-screen max-w-4xl px-4 py-16 text-base-content md:py-24 lg:px-0">
	<header class="mb-12 border-b border-base-content/10 pb-8">
		<h1 class="mb-4 text-4xl md:text-5xl lg:text-6xl">Privacy Policy</h1>
		<p class="text-md font-medium text-base-content/70">Last Updated: {LAST_UPDATED}</p>
	</header>

	<div class="flex flex-col gap-10 text-lg leading-relaxed font-light text-base-content/80">
		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">1. Introduction</h2>
			<p>
				Welcome to <strong>{SITE_NAME}</strong> ("we," "our," or "us"). We respect your privacy and are committed to protecting
				it. This Privacy Policy explains how we handle information when you visit our website at
				<a href={FULL_SITE_URL} class="text-primary hover:underline">{FULL_SITE_URL}</a>. It applies to visitors from anywhere in
				the world.
			</p>
			<p>
				We do not require user accounts, and we collect only minimal, non-identifying usage data. We believe in a privacy-first
				approach to web browsing.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">2. What Data We Collect</h2>
			<p>
				<strong>We do not ask for or collect your name, email, phone number or any account information.</strong> You can browse
				{SITE_NAME} without registering.
			</p>
			<p>
				<strong>Server Logs & Security:</strong> Our website is hosted and protected by Cloudflare. Cloudflare may temporarily process
				your IP address strictly for security purposes (to prevent DDoS attacks and malicious bots). This is a legitimate interest under
				the UK GDPR.
			</p>
			<p>
				<strong>Usage analytics:</strong> We also collect limited usage data about how the site is used, described in the next section.
				We do not store your IP address with this data.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">3. Analytics</h2>
			<p>We use two kinds of analytics to understand which guides are popular and how to improve the site.</p>

			<p>
				<strong>Cloudflare Web Analytics.</strong> This does not use client-side state (such as cookies or `localStorage`) to collect
				usage metrics. It does not track you across other websites or build a profile of your behaviour.
			</p>

			<p>
				<strong>Our own analytics.</strong> When you use the site, your browser sends small event messages to our server. These include:
			</p>
			<ul class="list-disc space-y-2 pl-6">
				<li>Which pages you viewed and when.</li>
				<li>How long you were actively on a page and how far down the page you scrolled.</li>
				<li>
					Interactions such as opening a city, using map filters, opening images or place details, reading more, toggling the
					theme, using the tour player, and clicking links to external sites (we record which link was clicked).
				</li>
				<li>
					Searches that returned no results, including a shortened version of the search term, so we know which destinations or
					topics to add. Terms that look like personal information (such as email addresses, web addresses or long numbers) are
					discarded. Please do not enter personal information in the search box.
				</li>
			</ul>
			<p>
				These events are linked only by a random session identifier that is generated each time the site loads and is kept in your
				browser's memory only. It is <strong>not stored on your device</strong>. Refreshing the page creates a new identifier, so
				we cannot recognise you across visits or build a long-term profile of you.
			</p>
			<p>
				<strong>We have no way of knowing which data belongs to which person.</strong> The data is not linked to your name, an account,
				your IP address or any other identifier, so we cannot tell who generated any particular event. We do not sell this data, share
				it for advertising, or use it to profile individuals.
			</p>
			<p>
				<strong>Retention:</strong> We automatically delete this analytics data every three months.
			</p>
			<p>
				<strong>Our legal basis</strong> for this analytics is our legitimate interest in understanding and improving the site (UK GDPR
				Art. 6(1)(f)).
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">4. Local Storage (Cookies)</h2>
			<p>
				While we do not use tracking cookies, we do use your browser's "Local Storage" to provide features you explicitly request.
			</p>
			<ul class="list-disc space-y-2 pl-6">
				<li><strong>Theme Preferences:</strong> We remember whether you prefer Light or Dark mode.</li>
				<li>
					<strong>Travel Passport:</strong> If you use our interactive map to mark cities you have visited, that data is saved directly
					to your device so it is there when you return.
				</li>
			</ul>
			<p>
				<strong>This data never leaves your device.</strong> It is never sent to our servers. Under the UK Privacy and Electronic Communications
				Regulations (PECR), this storage is considered "strictly necessary" to provide the functionality you requested. You can clear
				this data at any time by clearing your browser's cache.
			</p>
			<div class="divider"></div>
			<p>This site uses the Wikipedia API. Wikipedia may set cookies governed by the Wikimedia Privacy Policy.</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">5. Third-Party Links</h2>
			<p>
				Our city guides contain links to external websites (e.g., Wikipedia, YouTube, Wikimedia, and the official websites of
				attractions). We record which of these links are clicked, but we are not responsible for the privacy practices of those
				websites. We encourage you to read their privacy policies when you leave our site.
			</p>
		</section>

		<section class="flex flex-col gap-3">
			<h2 class="text-2xl text-base-content">6. Your Rights</h2>
			<p>
				Under the UK General Data Protection Regulation (UK GDPR), you have rights regarding your personal data (such as the right
				to access, delete, correct, or object to its processing). However, because we do not collect your name, account details or
				IP address with our analytics, and the session identifier is not kept on your device, we have no way to identify you or
				your device, or to work out which data relates to you. This means we are unable to fulfil such requests for the analytics
				data we hold.
			</p>
		</section>
	</div>
</div>
